import { apiService } from './api';
import { getUserTimezone } from '../utils/timezoneUtils';

export interface PushSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface NotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  data?: any;
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
}

class NotificationService {
  private vapidPublicKey: string | null = null;
  private subscription: PushSubscription | null = null;
  private isSupported: boolean;

  constructor() {
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  }

  /**
   * Check if push notifications are supported
   */
  isPushSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Get VAPID public key from backend
   */
  async getVapidPublicKey(): Promise<string> {
    if (this.vapidPublicKey) {
      return this.vapidPublicKey;
    }

    try {
      const response = await apiService.notifications.getVapidPublicKey();
      this.vapidPublicKey = response.data.data.publicKey;
      return this.vapidPublicKey!;
    } catch (error) {
      console.error('Failed to get VAPID public key:', error);
      throw new Error('Failed to get VAPID public key');
    }
  }

  /**
   * Request notification permission
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported) {
      throw new Error('Push notifications are not supported');
    }

    const permission = await Notification.requestPermission();
    return permission;
  }

  /**
   * Check current notification permission
   */
  getPermissionStatus(): NotificationPermission {
    if (!this.isSupported) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe(): Promise<PushSubscription> {
    if (!this.isSupported) {
      throw new Error('Push notifications are not supported');
    }

    // Check permission
    const permission = this.getPermissionStatus();
    if (permission !== 'granted') {
      throw new Error('Notification permission not granted');
    }

    // Check if we already have a subscription
    if (this.subscription) {
      return this.subscription;
    }

    // Try to retrieve existing subscription from backend first
    const existingSubscription = await this.retrieveExistingSubscription();
    if (existingSubscription) {
      return existingSubscription;
    }

    // Get VAPID public key
    const vapidPublicKey = await this.getVapidPublicKey();
    console.log('🔑 VAPID Public Key:', vapidPublicKey);

    // Convert VAPID key to Uint8Array
    const vapidKey = this.urlBase64ToUint8Array(vapidPublicKey);

    try {
      // Register service worker
      console.log('📱 Registering service worker...');
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('✅ Service worker registered:', registration);
      
      await navigator.serviceWorker.ready;
      console.log('✅ Service worker ready');

      // Check if push manager is supported
      if (!registration.pushManager) {
        throw new Error('Push manager not supported');
      }

      // Subscribe to push notifications
      console.log('📲 Subscribing to push notifications...');
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey,
      });
      console.log('✅ Push subscription created:', subscription);

      // Convert to our format
      this.subscription = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
          auth: this.arrayBufferToBase64(subscription.getKey('auth')!),
        },
      };

      console.log('📤 Sending subscription to backend:', this.subscription);

      // Send subscription to backend
      await this.sendSubscriptionToBackend(this.subscription);

      return this.subscription;
    } catch (error) {
      console.error('❌ Failed to subscribe to push notifications:', error);
      throw new Error('Failed to subscribe to push notifications');
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe(): Promise<void> {
    if (!this.isSupported || !this.subscription) {
      return;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }

      // Remove from backend
      await apiService.notifications.unsubscribe();

      this.subscription = null;
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      throw new Error('Failed to unsubscribe from push notifications');
    }
  }

  /**
   * Get current subscription
   */
  getCurrentSubscription(): PushSubscription | null {
    return this.subscription;
  }

  /**
   * Retrieve existing subscription from backend
   */
  async retrieveExistingSubscription(): Promise<PushSubscription | null> {
    if (!this.isSupported) {
      return null;
    }

    try {
      const response = await apiService.notifications.getCurrentSubscription();
      if (response.data.success && response.data.data.subscription) {
        this.subscription = response.data.data.subscription;
        return this.subscription;
      }
      return null;
    } catch (error: any) {
      // 404 means no subscription exists, which is fine
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to retrieve existing subscription:', error);
      return null;
    }
  }

  /**
   * Test notification (for development)
   */
  async testNotification(): Promise<void> {
    try {
      await apiService.notifications.test();
      console.log('Test notification sent');
    } catch (error) {
      console.error('Failed to send test notification:', error);
      throw error;
    }
  }

  /**
   * Send subscription to backend
   */
  private async sendSubscriptionToBackend(subscription: PushSubscription): Promise<void> {
    try {
      // Include user's timezone with the subscription
      const subscriptionData = {
        subscription,
        timezone: getUserTimezone()
      };
      
      await apiService.notifications.subscribe(subscriptionData);
      console.log('Subscription sent to backend with timezone:', getUserTimezone());
    } catch (error) {
      console.error('Failed to send subscription to backend:', error);
      throw error;
    }
  }



  /**
   * Convert VAPID key from base64 to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Convert ArrayBuffer to base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}

export const notificationService = new NotificationService();
